import type { Event as SentryEvent } from "@sentry/react";
import { isAxiosError } from "axios";

const ID_SEGMENT = /^(?:\d+|[\da-f]{8}-[\da-f]{4}-[\da-f]{4}-[\da-f]{4}-[\da-f]{12})$/i;

function normalizeRequestPath(url: string): string {
  // Relative for the Zodios client, absolute elsewhere.
  const [path = ""] = url.replace(/^[a-z]+:\/\/[^/]+/i, "").split(/[?#]/);
  return path
    .split("/")
    .map((segment) => (ID_SEGMENT.test(segment) ? ":id" : segment))
    .join("/");
}

function isUnhandled(event: SentryEvent): boolean {
  return event.exception?.values?.some((value) => value.mechanism?.handled === false) ?? false;
}

/**
 * A grouping key for a rejected request that nothing in the app caught.
 *
 * An axios rejection reaching `onunhandledrejection` has lost its call site — the trace holds axios
 * internals and nothing else — so Sentry's stack grouping puts every uncaught request failure in
 * one issue whatever the endpoint or status (TRELLIS-UI-3MN). Errors passed to `captureException`
 * still carry the frames of whatever reported them, so those keep Sentry's own grouping.
 */
export function apiErrorFingerprint(event: SentryEvent, error: unknown): string[] | undefined {
  if (!isAxiosError(error) || !error.response || !isUnhandled(event)) {
    return undefined;
  }
  const method = (error.config?.method ?? "unknown").toUpperCase();
  const path = error.config?.url === undefined ? "unknown" : normalizeRequestPath(error.config.url);
  return ["api-error", method, path, String(error.response.status)];
}
